"use client";

import { CelebrityAvatar } from "@/components/celebrity-avatar";
import { useFollowKeyword } from "@/hooks/use-follow-keyword";
import { useLoginGate } from "@/components/login-gate-dialog";
import { getAffinityTypeName, type TopicAffinityLabel } from "@/lib/topic-affinity";
import { Star, Loader2, MapPin, Tv, Zap, Building2, Hash } from "lucide-react";
import { trackAffinityClick, trackFollow } from "@/lib/analytics";

/** 各类型 affinity 的图标 */
const TYPE_ICONS: Record<string, React.ReactNode> = {
  person: null, // 用 CelebrityAvatar
  city: <MapPin className="h-3 w-3" />,
  country: null, // 用 label.icon (emoji flag)
  program: <Tv className="h-3 w-3" />,
  event: <Zap className="h-3 w-3" />,
  organization: <Building2 className="h-3 w-3" />,
  keyword: <Hash className="h-3 w-3" />,
};

interface AffinityChipProps {
  label: TopicAffinityLabel;
  className?: string;
}

/**
 * 通用 affinity 胶囊标签。
 * 胶囊主体：点击跳转专栏（名人页/城市页/搜索页等）。
 * 星星图标：点击关注/取关该关键词。
 *
 * 已关注时星星实心主色，未关注时星星描边。
 * 两部分点击独立，互不干扰。
 */
export function AffinityChip({ label, className }: AffinityChipProps) {
  const { following, toggle, loading } = useFollowKeyword(label.name);
  const { requireLogin, dialog: loginDialog } = useLoginGate({
    title: "登录后订阅",
    description: "登录即可订阅关键词，新动态第一时间邮件提醒。\n同时帮助我们防止机器人爬取，提供更优质体验。",
    icon: <Star className="w-5 h-5" />,
  });

  // 星星：点击关注/取关
  const handleStarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    requireLogin(async () => {
      await toggle();
      trackFollow(label.name, following ? "unfollow" : "follow", label.type);
    });
  };

  // 胶囊主体：跳转专栏
  const handleChipClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 不冒泡到父行(热搜词条行)
    trackAffinityClick(label.name, label.type);
  };

  return (
    <>
      <span
        className={`inline-flex items-center gap-0.5 rounded-full border text-[11px] font-medium transition-colors ${
          following
            ? "border-primary/30 bg-primary/5 text-foreground"
            : "border-transparent bg-muted text-muted-foreground hover:bg-muted/80"
        } ${className || ""}`}
      >
        <a
          href={label.href}
          onClick={handleChipClick}
          className="inline-flex items-center gap-1 px-2 py-0.5 transition-colors hover:text-primary"
          title={`${getAffinityTypeName(label.type)} · ${label.name} — 点击查看`}
        >
          {label.type === "person" ? (
            <CelebrityAvatar name={label.name} src={label.avatarUrl} size="xs" />
          ) : label.type === "country" && label.icon ? (
            <span className="text-xs leading-none">{label.icon}</span>
          ) : (
            TYPE_ICONS[label.type] || TYPE_ICONS.keyword
          )}
          <span className="max-w-[72px] truncate">{label.name}</span>
        </a>
        <button
          type="button"
          onClick={handleStarClick}
          className={`inline-flex items-center justify-center px-1.5 py-0.5 transition-colors rounded-r-full ${
            following
              ? "text-primary hover:bg-primary/10"
              : "text-muted-foreground/50 hover:text-primary"
          }`}
          title={following ? `取消订阅「${label.name}」` : `订阅「${label.name}」`}
        >
          {loading ? (
            <Loader2 className="h-2.5 w-2.5 animate-spin" />
          ) : (
            <Star className={`h-2.5 w-2.5 ${following ? "fill-primary" : ""}`} />
          )}
        </button>
      </span>
      {loginDialog}
    </>
  );
}
